import type { IViewportWrapperRequiredProps } from "@/src/master/ViewportInjector";
import ViewportWrapper from "@/src/master/ViewportInjector";
import { defaultAttributes } from "@Components/Component";
import type { IComponentAjax, IComponentAjaxState } from "@Components/ComponentAjax";
import { ComponentAjax } from "@Components/ComponentAjax";
import type { ILink } from "@Components/Link";
import { Link } from "@Components/Link";
import { Device } from "@Components/Modules/ViewportResize";

import type { ILoyaltyProgramItem } from "./LoyaltyProgramItem";
import { LoyaltyProgramItem } from "./LoyaltyProgramItem";

import * as cssPortal from "../shared/Portal.module.css";
import * as css from "./LoyaltyProgramsList.module.css";

export interface ILoyaltyProgramsList extends IComponentAjax<ILoyaltyProgramItem[]> {
    title: string;
    showAllLink?: ILink;
    cardsNumber?: number;
}

interface ILoyaltyProgramsListState extends IComponentAjaxState {
    loyaltyCards: ILoyaltyProgramItem[];
    buttonShowAll: boolean;
}

class LoyaltyProgramsListConnect extends ComponentAjax<
    ILoyaltyProgramItem[],
    ILoyaltyProgramsList & IViewportWrapperRequiredProps,
    ILoyaltyProgramsListState
> {
    constructor(props: ILoyaltyProgramsList & IViewportWrapperRequiredProps) {
        super(props);

        this.state = {
            isLoading: !this.props.initialData,
            loyaltyCards: [],
            buttonShowAll: false,
        };
    }

    public componentDidMount() {
        if (this.props.initialData) {
            this.setState({ loyaltyCards: this.props.initialData });
            return;
        }

        this.ajaxRequest();
    }

    public render() {
        const buttonShowAll = () =>
            this.state.buttonShowAll && (
                <div className={css.buttonShowAll}>
                    <Link {...this.props.showAllLink} onClick={this.showAllCards} />
                </div>
            );

        return (
            <div
                {...defaultAttributes(
                    this.props,
                    css.loyaltyProgramsListComponent,
                    cssPortal.standardContainer,
                )}
            >
                <h1 className={css.title}>{this.props.title}</h1>
                {this.renderLoyaltyCardsList()}
                {buttonShowAll()}
                {this.renderLoading()}
                {this.renderAjaxErrorContainer()}
            </div>
        );
    }

    protected ajaxSuccess(response: ILoyaltyProgramItem[]) {
        if (!response || response.length === 0) {
            this.setState({ isLoading: false, buttonShowAll: false, loyaltyCards: [] });
            return;
        }

        this.setState({ isLoading: false, buttonShowAll: true, loyaltyCards: response });
    }

    protected ajaxFail() {
        this.setState({ isLoading: false, buttonShowAll: false, loyaltyCards: [] });
        return true;
    }

    private showAllCards = () => this.setState({ buttonShowAll: false });

    private renderLoyaltyCardsList() {
        if (!this.state.loyaltyCards || this.state.loyaltyCards.length === 0) {
            return null;
        }

        const list = !this.state.buttonShowAll
            ? this.state.loyaltyCards
            : this.state.loyaltyCards.slice(0, this.props.cardsNumber || 5);

        const isMobile = this.props.device === Device.Mobile;

        return (
            <div className={css.cardsList}>
                {list.map((item, i) => (
                    <LoyaltyProgramItem {...item} isMobile={isMobile} key={i} />
                ))}
            </div>
        );
    }
}

export const LoyaltyProgramsList = (props: ILoyaltyProgramsList) => (
    <ViewportWrapper Component={LoyaltyProgramsListConnect} props={props} />
);
